import { Command } from 'cmdk';
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CubeIcon,
  ActivityLogIcon,
  FileTextIcon,
  CodeIcon,
  ListBulletIcon,
  PlusIcon,
  GearIcon,
  MoonIcon,
  SunIcon,
  MagnifyingGlassIcon,
  RocketIcon,
  PersonIcon,
  LockClosedIcon,
  MixIcon,
} from '@radix-ui/react-icons';
import { useTheme } from '../../contexts/ThemeContext';
import './styles.css';

interface CommandPaletteProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const CommandPalette = ({
  open: controlledOpen,
  onOpenChange,
}: CommandPaletteProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const { theme, setTheme, effectiveTheme } = useTheme();

  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  // Toggle the palette with keyboard shortcut
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen(!open);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [open, setOpen]);

  // Reset search when closing
  useEffect(() => {
    if (!open) {
      setSearch('');
    }
  }, [open]);

  const runCommand = useCallback(
    (command: () => void) => {
      setOpen(false);
      command();
    },
    [setOpen],
  );

  const handleNavigate = useCallback(
    (path: string) => {
      runCommand(() => navigate(path));
    },
    [navigate, runCommand],
  );

  const handleThemeToggle = useCallback(() => {
    runCommand(() => {
      if (theme === 'system') {
        setTheme(effectiveTheme === 'dark' ? 'light' : 'dark');
      } else {
        setTheme(theme === 'dark' ? 'light' : 'dark');
      }
    });
  }, [theme, effectiveTheme, setTheme, runCommand]);

  const handleSetTheme = useCallback(
    (newTheme: 'light' | 'dark' | 'system') => {
      runCommand(() => setTheme(newTheme));
    },
    [setTheme, runCommand],
  );

  return (
    <Command.Dialog
      open={open}
      onOpenChange={setOpen}
      label="Command Palette"
      className={`command-palette ${effectiveTheme === 'dark' ? 'dark' : 'light'}`}
    >
      <div className="command-palette-header">
        <MagnifyingGlassIcon className="command-palette-search-icon" />
        <Command.Input
          value={search}
          onValueChange={setSearch}
          placeholder="Type a command or search..."
          className="command-palette-input"
        />
        <kbd className="command-palette-shortcut">ESC</kbd>
      </div>

      <Command.List className="command-palette-list">
        <Command.Empty className="command-palette-empty">
          No results found.
        </Command.Empty>

        <Command.Group heading="Navigation" className="command-palette-group">
          <Command.Item
            onSelect={() => handleNavigate('/blocks')}
            className="command-palette-item"
          >
            <CubeIcon className="command-palette-item-icon" />
            <span>Go to Blocks</span>
          </Command.Item>
          <Command.Item
            onSelect={() => handleNavigate('/transactions')}
            className="command-palette-item"
          >
            <ActivityLogIcon className="command-palette-item-icon" />
            <span>Go to Transactions</span>
          </Command.Item>
          <Command.Item
            onSelect={() => handleNavigate('/events')}
            className="command-palette-item"
          >
            <FileTextIcon className="command-palette-item-icon" />
            <span>Go to Events</span>
          </Command.Item>
          <Command.Item
            onSelect={() => handleNavigate('/contracts')}
            className="command-palette-item"
          >
            <CodeIcon className="command-palette-item-icon" />
            <span>Go to Contracts</span>
          </Command.Item>
          <Command.Item
            onSelect={() => handleNavigate('/workflows')}
            className="command-palette-item"
          >
            <ListBulletIcon className="command-palette-item-icon" />
            <span>Go to Workflows</span>
          </Command.Item>
          <Command.Item
            onSelect={() => handleNavigate('/graphiql')}
            className="command-palette-item"
          >
            <MixIcon className="command-palette-item-icon" />
            <span>Go to GraphiQL</span>
          </Command.Item>
        </Command.Group>

        <Command.Group heading="Actions" className="command-palette-group">
          <Command.Item
            onSelect={() => handleNavigate('/workflows/create')}
            className="command-palette-item"
          >
            <PlusIcon className="command-palette-item-icon" />
            <span>Create New Workflow</span>
          </Command.Item>
        </Command.Group>

        <Command.Group heading="Settings" className="command-palette-group">
          <Command.Item
            onSelect={() => handleNavigate('/settings')}
            className="command-palette-item"
          >
            <GearIcon className="command-palette-item-icon" />
            <span>Open Settings</span>
          </Command.Item>
          <Command.Item
            onSelect={() => handleNavigate('/settings/profile')}
            className="command-palette-item"
          >
            <PersonIcon className="command-palette-item-icon" />
            <span>Edit Profile</span>
          </Command.Item>
          <Command.Item
            onSelect={() => handleNavigate('/settings/api-keys')}
            className="command-palette-item"
          >
            <LockClosedIcon className="command-palette-item-icon" />
            <span>Manage API Keys</span>
          </Command.Item>
          <Command.Item
            onSelect={() => handleNavigate('/settings/integrations')}
            className="command-palette-item"
          >
            <RocketIcon className="command-palette-item-icon" />
            <span>Manage Integrations</span>
          </Command.Item>
        </Command.Group>

        <Command.Group heading="Theme" className="command-palette-group">
          <Command.Item
            onSelect={handleThemeToggle}
            className="command-palette-item"
          >
            {effectiveTheme === 'dark' ? (
              <SunIcon className="command-palette-item-icon" />
            ) : (
              <MoonIcon className="command-palette-item-icon" />
            )}
            <span>
              Toggle Theme ({effectiveTheme === 'dark' ? 'Light' : 'Dark'})
            </span>
          </Command.Item>
          <Command.Item
            onSelect={() => handleSetTheme('light')}
            className="command-palette-item"
          >
            <SunIcon className="command-palette-item-icon" />
            <span>Set Light Theme</span>
          </Command.Item>
          <Command.Item
            onSelect={() => handleSetTheme('dark')}
            className="command-palette-item"
          >
            <MoonIcon className="command-palette-item-icon" />
            <span>Set Dark Theme</span>
          </Command.Item>
          <Command.Item
            onSelect={() => handleSetTheme('system')}
            className="command-palette-item"
          >
            <GearIcon className="command-palette-item-icon" />
            <span>Use System Theme</span>
          </Command.Item>
        </Command.Group>
      </Command.List>
    </Command.Dialog>
  );
};

export default CommandPalette;
